import React, { useEffect, useState } from "react";
import * as ReactDOMServer from "react-dom/server";

import PropTypes from "prop-types";
import styles from "./TextEditor.module.css";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css"; // or 'quill/dist/quill.bubble.css' if using the Bubble theme
import TurndownService from "turndown";
import Markdown from "../../Shared/Markdown";
import parse, { domToReact } from "html-react-parser";
import Delta from "quill-delta";

import { nanoid } from "nanoid";

/**
 * A rich text editor that returns markdown through the setText prop.
 * text prop accepts markdown or HTML for initial value.
 * Markdown function within ManagementTeam/Shared/Markdown returns markdown as HTML.
 * See stories for example implementation.
 */

export function TextEditor({ text, setText }) {
  // turndown options
  const turndownService = new TurndownService({
    emDelimiter: "*",
    strongDelimiter: "**",
    br: "linebreaktag",
  });
  turndownService.keep(["sub", "sup", "mark", "u"]);

  const id = "notANumber" + nanoid();

  // text editor options
  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: {
        container: "#" + id,
      },
    },
    formats: [
      "bold",
      "italic",
      "underline",
      "script",
      "list",
      "header",
      "background",
    ],
  });

  useEffect(() => {
    if (quill) {
      // finds <mark> tags and converts them to a format the Quilljs likes
      quill.clipboard.addMatcher("mark", function (node, delta) {
        return delta.compose(
          new Delta().retain(delta.length(), { background: "#FFFF00" })
        );
      });

      // set initial contents
      quill.clipboard.dangerouslyPasteHTML(
        ReactDOMServer.renderToString(<Markdown content={text ? text : ""} />)
      );

      // onChange
      quill.on("text-change", () => {
        const html = quill.root.innerHTML;
        const options = {
          replace: ({ attribs, name, children }) => {
            const style = "background-color: rgb(255, 255, 0);";

            const Template = ({ name, children, options }) => {
              return React.createElement(
                name,
                null,
                <mark>{domToReact(children, options)}</mark>
              );
            };

            if (!attribs) {
              return;
            }
            if (attribs.style === style && name === "span") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
            if (attribs.style === style && name === "strong") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
            if (attribs.style === style && name === "em") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
            if (attribs.style === style && name === "sub") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
            if (attribs.style === style && name === "sup") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
            if (attribs.style === style && name === "u") {
              return (
                <Template name={name} children={children} options={options} />
              );
            }
          },
        };

        // converts contents to markdown
        var content = turndownService.turndown(
          ReactDOMServer.renderToStaticMarkup(parse(html, options))
        );
        setText(
          content
            .replace(/<br\/>/g, "linebreaktag")
            .replace(/<strong>|<\/strong>/g, "**")
            .replace(/<em>|<\/em>/g, "*")
        );
      });
    }

    //this may run multiple times, but it doesn't matter
    setTimeout(function () {
      let labels = window.document.getElementsByClassName("ql-picker-label");
      if (labels && labels.length > 0) {
        for (var i = 0; i < labels.length; i++) {
          labels[i].tabIndex = -1; //disable tabIndex on Heading select
        }
      }
    }, 1000);
  }, [quill]);

  // for clearing text from the parent
  useEffect(() => {
    if (quill && text == "") {
      quill.deleteText(0, quill.getLength());
    }
  }, [text]);

  // Custom highlight button
  const [color, setColor] = useState(null);
  const handleHighlight = () => {
    color === "#FFFF00" ? setColor(null) : setColor("#FFFF00");
  };
  useEffect(() => {
    setColor("#FFFF00");
  }, []);

  return (
    <div className={styles.container}>
      <div id={id}>
        <span className="ql-formats">
          <button className="ql-bold ql-active" tabIndex={-1} />
          <button className="ql-italic" tabIndex={-1} />
          <button className="ql-underline" tabIndex={-1} />
        </span>

        <span className="ql-formats">
          <button
            className="ql-background"
            value={color}
            onClick={handleHighlight}
            tabIndex={-1}
          />
        </span>

        <span className="ql-formats">
          <button className="ql-script" value="super" tabIndex={-1} />
          <button className="ql-script" value="sub" tabIndex={-1} />
        </span>

        <span className="ql-formats">
          <button className="ql-list" value="bullet" tabIndex={-1} />
          <button className="ql-list" value="ordered" tabIndex={-1} />
        </span>

        <span className="ql-formats" tabIndex={-1}>
          <select className="ql-header" defaultValue="false" tabIndex={-1}>
            <option value="1" tabIndex={-1}>
              h1
            </option>
            <option value="2" tabIndex={-1}>
              h2
            </option>
            <option value="3" tabIndex={-1}>
              h3
            </option>
            <option value="4" tabIndex={-1}>
              h4
            </option>
            <option value="5" tabIndex={-1}>
              h5
            </option>
            <option value="6" tabIndex={-1}>
              h6
            </option>
            <option value="false" tabIndex={-1} selected>
              Normal
            </option>
          </select>
        </span>
      </div>
      <div id="editor" />
      <div ref={quillRef} />
    </div>
  );
}

TextEditor.propTypes = {
  // The text displayed in the text box
  text: PropTypes.string,

  // Sets the text displayed in the text box when updated
  setText: PropTypes.func,
};
